import { memo, type ReactNode, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';

import { PlayerContextProvider } from '@air/component-player';
import { LinkIcon } from '@air/design-icons';
import { type ContextMenuOption } from '@air/primitive-context-menu';

import { AssetModalContentDesktopLayout } from '~/components/AssetModalV2/AssetModalContentDesktopLayout';
import { AssetModalDesktopLayout } from '~/components/AssetModalV2/AssetModalDesktopLayout';
import { PublicAssetModalLeftPanelContent } from '~/components/AssetModalV2/Content/Panel/components/PublicAssetModalLeftPanelContent';
import { PublicAssetNavigationArrows } from '~/components/AssetModalV2/Header/PublicAssetComponents/PublicAssetNavigationArrows';
import {
  type UseClosePublicAssetModalProps,
  useClosePublicAssetModal,
} from '~/components/AssetModalV2/hooks/useClosePublicAssetModal';
import { usePublicAssetNavigationHotkeys } from '~/components/AssetModalV2/hooks/usePublicAssetNavigationHotkeys';
import { PublicAssetInfoPanel } from '~/components/AssetModalV2/InfoPanel/PublicAssetInfoPanel';
import { PublicRiveVisualizer } from '~/components/AssetModalV2/Visualizer/components/RiveVisualizer/PublicRiveVisualizer';
import { PublicImageVisualizer } from '~/components/AssetModalV2/Visualizer/ImageVisualizer/PublicImageVisualizer';
import { PublicPDFVisualizer } from '~/components/AssetModalV2/Visualizer/PDFVisualizer/PublicPDFVisualizer';
import { FullscreenOverlay } from '~/components/AssetModalV2/Visualizer/shared/components/FullscreenOverlay';
import { Visualizer } from '~/components/AssetModalV2/Visualizer/Visualizer';
import { PublicAssetModalContentFooter } from '~/components/v2/components/asset-modal/desktop/content/footer/PublicAssetModalContentFooter';
import { PublicAssetModalContentHeader } from '~/components/v2/components/asset-modal/desktop/content/header/PublicAssetModalContentHeader';
import { PublicVersionsPanel } from '~/components/v2/components/asset-modal/desktop/versions/PublicVersionsPanel';
import { PublicVideoVisualizer } from '~/components/v2/components/asset-modal/visualizer/video/PublicVideoVisualizer';
import { ZustandStorageKeys } from '~/constants/zustandStorageKeys';
import { centralizedClipSelector } from '~/store/centralizedClip/selectors';

export type PublicAssetModalDesktopLayoutProps = {
  header: ReactNode;
  onShare: () => void;
  showFullscreenNavigationArrows: boolean;
} & Pick<UseClosePublicAssetModalProps, 'onCloseAssetModal'>;

export const PublicAssetModalDesktopLayout = memo(
  ({ header, onCloseAssetModal, onShare, showFullscreenNavigationArrows }: PublicAssetModalDesktopLayoutProps) => {
    usePublicAssetNavigationHotkeys();
    const clip = useSelector(centralizedClipSelector);
    const { closeAssetModal } = useClosePublicAssetModal({ onCloseAssetModal, trackLocation: 'esc-key' });

    const options: ContextMenuOption[] = useMemo(
      () => [
        {
          label: 'Share a link',
          id: 'share-link',
          type: 'item',
          prefix: <LinkIcon size="sm" />,
          onSelect: onShare,
        },
      ],
      [onShare],
    );

    const renderVersionsPanel = useCallback(() => {
      return <PublicVersionsPanel />;
    }, []);

    const renderRightPanel = useCallback(() => {
      return <PublicAssetInfoPanel />;
    }, []);

    return (
      <PlayerContextProvider id={clip.id} storageKey={ZustandStorageKeys.AssetModalPlayer}>
        <AssetModalDesktopLayout
          content={
            <AssetModalContentDesktopLayout
              content={
                <Visualizer
                  ImageVisualizer={PublicImageVisualizer}
                  VideoVisualizer={PublicVideoVisualizer}
                  PDFVisualizer={PublicPDFVisualizer}
                  RiveVisualizer={PublicRiveVisualizer}
                />
              }
              footer={<PublicAssetModalContentFooter />}
              fullscreenOverlay={
                <FullscreenOverlay
                  navigationArrows={showFullscreenNavigationArrows ? <PublicAssetNavigationArrows /> : null}
                  modeToggleSegmentedControl={null}
                  showModeToggle={false}
                />
              }
              header={<PublicAssetModalContentHeader />}
              leftPanel={<PublicAssetModalLeftPanelContent />}
            />
          }
          contextMenuOptions={options}
          header={header}
          onClose={closeAssetModal}
          renderVersionsPanel={renderVersionsPanel}
          renderRightPanel={renderRightPanel}
        />
      </PlayerContextProvider>
    );
  },
);

PublicAssetModalDesktopLayout.displayName = 'PublicAssetModalDesktopLayout';
